import { Box, BoxProps } from '@material-ui/core';

import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { IconWithCircle } from '@/components/shared/components/IconWithCircle';
import { Text } from '@/components/shared/components/Text';
import { CustomPalette, PendingDataType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const PendingDataWarning = ({
  type = PendingDataType.DEFAULT,
  tId,
  iconBackgroundColor,
  boxProps,
}: {
  type?: PendingDataType;
  tId?: string;
  iconBackgroundColor?: CustomPalette;
  boxProps?: BoxProps;
}) => {
  const { palette } = useCustomTheme();

  const PENDING_DATA_ELEMENTS_BY_TYPE = {
    [PendingDataType.DEFAULT]: {
      icon: 'hide_source',
      infoMessage: 'NO_INFORMATION_PROVIDED',
    },
    [PendingDataType.NO_DATA_TO_DISPLAY]: {
      icon: 'hide_source',
      infoMessage: 'NO_DATA_TO_DISPLAY',
    },
    [PendingDataType.BORROWER_ONBOARDING]: {
      icon: 'schedule',
      infoMessage: 'PENDING_INFORMATION_FROM_BORROWER_ONBOARDING',
    },
    [PendingDataType.BORROWER_DOCUMENTATION]: {
      icon: 'badge',
      infoMessage: 'PENDING_DOCUMENTATION_FROM_BORROWER_ONBOARDING',
    },
    [PendingDataType.BORROWER_FINANCIAL_INFORMATION]: {
      icon: 'payments',
      infoMessage: 'PENDING_FOR_FINANCIAL_INFORMATION_FROM_BORROWER_ONBOARDING',
    },
    [PendingDataType.ADDRESS]: {
      icon: 'local_shipping',
      infoMessage: 'PENDING_FOR_ADDRESS_INFORMATION',
    },
    [PendingDataType.CREDENTIALS]: {
      icon: 'key_off',
      infoMessage: 'NO_CREDENTIALS_SET',
    },
    [PendingDataType.AGENT_PRICINGS]: {
      icon: 'monetization_on',
      infoMessage: 'NO_AGENT_PRICINGS',
    },
    [PendingDataType.PAYMENT_PLAN]: {
      icon: 'credit_card_off',
      infoMessage: 'NO_PAYMENT_PLAN_AVAILABLE',
    },
    [PendingDataType.PAYMENT_ATTEMPTS]: {
      icon: 'credit_card_off',
      infoMessage: 'NO_PAYMENT_ATTEMPTS_REGISTERED',
    },
    [PendingDataType.KYC_VERIFICATION]: {
      icon: 'pending_actions',
      infoMessage: 'PENDING_FOR_KYC_VERIFICATION',
    },
    [PendingDataType.RISK_ANALYSIS]: {
      icon: 'no_accounts',
      infoMessage: 'NO_RISK_SCORING_AVAILABLE',
    },
    [PendingDataType.CART]: {
      icon: 'remove_shopping_cart',
      infoMessage: 'NO_CART_INFORMATION_AVAILABLE',
    },
    [PendingDataType.ACTIVITY]: {
      icon: 'credit_card_off',
      infoMessage: 'NO_ACTIVITY_INFORMATION_AVAILABLE',
    },
    [PendingDataType.CUSTOMER_SUPPORT]: {
      icon: 'speaker_notes_off',
      infoMessage: 'NO_TICKETS_AVAILABLE',
    },
    [PendingDataType.NO_CHANNELS_SELECTED]: {
      icon: 'playlist_remove',
      infoMessage: 'NO_CHANNELS_SELECTED',
    },
    [PendingDataType.NO_METRICS_INFORMATION]: {
      icon: 'money_off',
      infoMessage: 'NO_METRICS_INFORMATION',
    },
    [PendingDataType.NO_RISK_INFORMATION]: {
      icon: 'remove_moderator',
      infoMessage: 'NO_RISK_INFORMATION',
    },
    [PendingDataType.NO_REASONS_GIVEN]: {
      icon: 'playlist_remove',
      infoMessage: 'NO_REASONS_GIVEN',
    },
    [PendingDataType.PASSWORD_RECOVERY_NOT_FOUND]: {
      icon: 'search_off',
      infoMessage: 'PASSWORD_RECOVERY_NOT_FOUND',
    },
    [PendingDataType.COMPLIANCE]: {
      icon: 'pending_actions',
      infoMessage: 'COMPLIANCE_CONTRACT_PENDING',
    },
    [PendingDataType.PAYOUT_DETAILS]: {
      icon: 'credit_card_off',
      infoMessage: 'PAYOUT_DETAILS_NOT_FOUND',
    },
    [PendingDataType.NO_PAYMENT_INTENTS]: {
      icon: 'credit_card_off',
      infoMessage: 'NO_PAYMENT_INTENTS',
    },
    [PendingDataType.NO_WEBHOOK_CONFIGURED]: {
      icon: 'code_off',
      infoMessage: 'NO_WEBHOOK_CONFIGURED',
    },
    [PendingDataType.NO_FUNNEL_DATA]: {
      icon: 'grid_off',
      infoMessage: 'NO_FUNNEL_DATA',
    },
    [PendingDataType.NO_PRICINGS_AVAILABLE]: {
      icon: 'monetization_on',
      infoMessage: 'NO_PRICINGS_AVAILABLE',
    },
    [PendingDataType.NO_AGENTS_IN_CHANNEL]: {
      icon: 'person_off',
      infoMessage: 'NO_AGENTS_IN_CHANNEL',
    },
    [PendingDataType.NO_NOTIFICATIONS]: {
      icon: 'notifications_off',
      infoMessage: 'NO_NOTIFICATIONS',
    },
    [PendingDataType.NO_DOCUMENTATION_FOUND]: {
      icon: 'badge',
      infoMessage: 'NO_DOCUMENTATION_FOUND',
    },
    [PendingDataType.NO_LOAN_FOUND]: {
      icon: 'schedule',
      infoMessage: 'NO_LOAN_FOUND',
    },
    [PendingDataType.CANCELLED_CARD]: {
      icon: 'credit_card_off',
      infoMessage: 'CANCELLED_CARD',
    },
    [PendingDataType.NO_LENDER_CONFIGURATION_FOUND]: {
      icon: 'sync_disabled',
      infoMessage: 'LENDER_ORCHESTRATION_LOAD_ERROR',
    },
  };

  const backgroundColor = iconBackgroundColor || palette.grey[100];

  return (
    <CustomPaper fill="grey.light">
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'center'}
        alignItems={'center'}
        gridGap={'20px'}
        style={{ minHeight: '40px' }}
        padding={1}
        {...boxProps}
      >
        <IconWithCircle
          height={40}
          width={40}
          bgcolor={'transparent'}
          className="material-icons-outlined"
          style={{
            opacity: 0.25,
            color: palette.getContrastText(backgroundColor.toString()),
            fontSize: 24,
          }}
        >
          {PENDING_DATA_ELEMENTS_BY_TYPE[type].icon}
        </IconWithCircle>
        <Box style={{ opacity: 0.7 }}>
          <Font variant="subtitle2" color="textSecondary">
            <Text tId={tId || PENDING_DATA_ELEMENTS_BY_TYPE[type].infoMessage} />
          </Font>
        </Box>
      </Box>
    </CustomPaper>
  );
};
